import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  output
} from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { NgClass } from '@angular/common';

dayjs.extend(localeData);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-date-range',
  templateUrl: './m-date-range.component.html',
  imports: [NgClass]
})
export class MDateRangeComponent implements OnInit {
  @Input() selectedYear: number = dayjs().year();
  @Input() minDate: Date = dayjs().subtract(1, 'year').toDate();
  @Input() maxDate: Date = dayjs().subtract(1, 'month').toDate();
  @Input() resolution: 'month' | 'quarter' = 'month';
  @Input() selectedDate: Dayjs | { start: Dayjs; end: Dayjs } | null = null;

  private minDayjs!: Dayjs;
  private maxDayjs!: Dayjs;

  periods: string[] = [];
  private startDate!: Dayjs;
  private endDate!: Dayjs;
  public selectedPeriod: number | null = null;

  readonly yearChanged = output<number>();
  readonly dateSelected = output<{
    start: Dayjs;
    end: Dayjs;
  }>();

  ngOnInit() {
    this.minDayjs = dayjs(this.minDate);
    this.maxDayjs = dayjs(this.maxDate);
    this.updatePeriods();

    if (this.selectedDate) {
      if ('start' in this.selectedDate && 'end' in this.selectedDate) {
        this.startDate = this.selectedDate.start;
        this.endDate = this.selectedDate.end;
      } else if (this.selectedDate instanceof dayjs) {
        this.startDate = this.selectedDate;
        this.endDate =
          this.resolution === 'month'
            ? this.startDate.endOf('month')
            : this.startDate.add(2, 'months').endOf('month');
      }
    } else {
      this.startDate = this.minDayjs;
      this.endDate =
        this.resolution === 'month'
          ? this.startDate.endOf('month')
          : this.startDate.add(2, 'months').endOf('month');
    }

    this.selectedPeriod = this.getPeriodIndexForDate(this.startDate);
    this.emitDateRange();
  }

  goToPreviousYear(): void {
    if (this.selectedYear > this.minDayjs.year()) {
      this.selectedYear--;
      this.emitYearChange();
    }
  }

  goToNextYear(): void {
    if (this.selectedYear < this.maxDayjs.year()) {
      this.selectedYear++;
      this.emitYearChange();
    }
  }

  private emitYearChange(): void {
    this.yearChanged.emit(this.selectedYear);
    this.updatePeriods();
  }

  updatePeriods(): void {
    this.periods =
      this.resolution === 'month'
        ? dayjs.localeData().monthsShort()
        : [
            'January - March',
            'April - June',
            'July - September',
            'October - December'
          ];
  }

  isPeriodDisabled(periodIndex: number): boolean {
    return this.resolution === 'month'
      ? this.isMonthDisabled(periodIndex)
      : this.isQuarterDisabled(periodIndex);
  }

  private isMonthDisabled(monthIndex: number): boolean {
    return this.isOutsideMinMaxRange(monthIndex, monthIndex);
  }

  private isQuarterDisabled(quarterIndex: number): boolean {
    const quarterStartMonth = quarterIndex * 3;
    const quarterEndMonth = quarterStartMonth + 2;
    return this.isOutsideMinMaxRange(quarterStartMonth, quarterEndMonth);
  }

  private isOutsideMinMaxRange(startMonth: number, endMonth: number): boolean {
    const isBeforeMinDate =
      this.selectedYear === this.minDayjs.year() &&
      endMonth < this.minDayjs.month();
    const isAfterMaxDate =
      this.selectedYear === this.maxDayjs.year() &&
      startMonth > this.maxDayjs.month();

    const isPartiallyBeforeMinDate =
      this.selectedYear === this.minDayjs.year() &&
      startMonth < this.minDayjs.month();
    const isPartiallyAfterMaxDate =
      this.selectedYear === this.maxDayjs.year() &&
      endMonth > this.maxDayjs.month();

    return (
      isBeforeMinDate ||
      isAfterMaxDate ||
      isPartiallyBeforeMinDate ||
      isPartiallyAfterMaxDate
    );
  }

  onPeriodSelect(periodIndex: number): void {
    if (!this.isPeriodDisabled(periodIndex)) {
      this.selectedPeriod = periodIndex;
      this.startDate = this.createDateForPeriod(periodIndex);
      this.endDate =
        this.resolution === 'month'
          ? this.startDate.endOf('month')
          : this.startDate.add(2, 'months').endOf('month');
      this.emitDateRange();
    }
  }

  private createDateForPeriod(periodIndex: number): Dayjs {
    const month = this.resolution === 'month' ? periodIndex : periodIndex * 3;
    return dayjs().year(this.selectedYear).month(month).date(1);
  }

  private emitDateRange(): void {
    if (this.startDate && this.endDate) {
      this.dateSelected.emit({ start: this.startDate, end: this.endDate });
    }
  }

  getNavigationClass(direction: 'prev' | 'next'): string {
    const canNavigate =
      direction === 'prev'
        ? this.selectedYear > this.minDayjs.year()
        : this.selectedYear < this.maxDayjs.year();
    return canNavigate
      ? 'text-blue-600 cursor-pointer'
      : 'text-gray-600 cursor-not-allowed';
  }

  getPeriodClass(index: number): string {
    if (this.isPeriodDisabled(index)) {
      return 'border-transparent text-gray-600 cursor-not-allowed';
    } else if (
      this.startDate &&
      this.startDate.month() ===
        (this.resolution === 'month' ? index : index * 3) &&
      this.startDate.year() === this.selectedYear
    ) {
      return 'border-blue-600';
    } else {
      return 'border-gray-000 bg-gray-000';
    }
  }

  getGridClass(): string {
    return this.resolution === 'month'
      ? 'grid-cols-4 gap-4'
      : 'grid-cols-2 gap-8';
  }

  private getPeriodIndexForDate(date: Dayjs): number {
    return this.resolution === 'month'
      ? date.month()
      : Math.floor(date.month() / 3);
  }
}
